/*
 * PageWright — Image search (S3), vanilla JS, no framework.
 *
 * Behavior:
 *   - Products mode (default): the <form> submits natively to /search?q=…&type=product.
 *     JS does nothing special — progressive enhancement, works with JS off too.
 *   - Images mode: JS intercepts submit, fetches {backend_url}/api/search?q=…&type=page,
 *     and renders result cards into the results container. No page reload.
 *
 * API shape (from @pagewright/contracts SearchResponseSchema / PageResultSchema):
 *   { results: [ { id, type:'page', title, coverImage?, parentHandle, pageNumber, price? } ], total, page, pages, facets }
 *   price is a Shopify MoneyV2: { amount: "2.00", currencyCode: "USD" }.
 *
 * S4 hook: each "Add page" button is rendered disabled and carries data-* attributes
 *   (data-page-id, data-parent-handle, data-page-number, data-variant, data-price-amount,
 *   data-price-currency). S4 (single-image add-to-cart) will enable these and wire them to
 *   /cart/add.js. No cart logic lives here yet.
 */
(function () {
  'use strict';

  var CURRENCY_SYMBOLS = { USD: '$', CAD: '$', AUD: '$', GBP: '£', EUR: '€' };

  // Paul, 2026-08-15 (Search Ledger review): these are catalog-format words, not visual content —
  // "freebie"/"freebies"/"free books" typed into Images mode should land on the same $0-product
  // results a Products-mode search for the same term already returns (confirmed live: "freebies"
  // returns 76 Shopify product results), not an image search. Mirrors PageWright's own
  // search-engine.ts `pageExempt` exclusion for the same words, on the frontend side.
  var FORCE_PRODUCT_TERMS = ['freebie', 'freebies', 'free books'];

  function forcesProductResults(query) {
    return FORCE_PRODUCT_TERMS.indexOf((query || '').trim().toLowerCase()) !== -1;
  }

  function formatMoney(price) {
    if (!price || typeof price.amount === 'undefined') return '';
    var symbol = CURRENCY_SYMBOLS[price.currencyCode] || '';
    var n = Number(price.amount);
    if (!isFinite(n)) return '';
    // Drop a trailing ".00" so $2.00 reads as "$2"; keep cents otherwise.
    var text = n % 1 === 0 ? String(n) : n.toFixed(2);
    return symbol + text;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Phase 2A (2026-07-20): search/click analytics for image search, posted to PageWright's
  // already-built public beacon (`packages/api/src/collect.ts` -> Neon `cs_analytics`). Reuses
  // the SAME sessionId/_ga-derived clientId keys the cart/GA client-id work already established
  // (Phase 1A, packages/web/src/ga-client-id.ts) so rows here join to the same session/user.
  // Fire-and-forget by design (this endpoint's own doc comment): never blocks or degrades the
  // search UI on failure, so plain sendBeacon with no response handling is correct here.
  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function postCollectEvent(backendUrl, payload) {
    if (!backendUrl) return;
    try {
      var body = JSON.stringify(Object.assign(
        { eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() },
        payload
      ));
      var url = backendUrl + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break the search UI */
    }
  }

  function setStatus(statusEl, message) {
    if (!statusEl) return;
    if (message) {
      statusEl.textContent = message;
      statusEl.hidden = false;
    } else {
      statusEl.textContent = '';
      statusEl.hidden = true;
    }
  }

  function buildCard(result, variantId) {
    var card = el('div', 'pw-image-search__card');
    // Phase 2A: click-analytics identity for this card, read by the delegated select_item
    // listener below — same {parentHandle}:{pageNumber} shape already used by the add-to-cart
    // button's data-page-id, so click and add-to-cart rows join on the same handle.
    card.setAttribute('data-page-id', result.id || '');

    // Cover tile (lazy image, alt = title). textContent-safe title used for alt.
    var tile = el('div', 'pw-image-search__tile');
    if (result.coverImage) {
      var img = el('img');
      img.src = result.coverImage;
      img.alt = result.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      tile.appendChild(img);
    }
    // Favorites, Phase 2 (2026-07-21): explicit page-kind mount, reusing the same
    // parentHandle/pageNumber identity already stamped on this card's add-to-cart button —
    // no new data plumbing (assets/pw-favorites.js's regression guard against DOM-guessing).
    if (result.parentHandle && result.pageNumber != null) {
      var favBtn = el('button', 'pw-heart');
      favBtn.type = 'button';
      favBtn.setAttribute('data-pw-fav', '');
      favBtn.setAttribute('data-pw-fav-kind', 'page');
      favBtn.setAttribute('data-pw-fav-handle', result.parentHandle);
      favBtn.setAttribute('data-pw-fav-page', String(result.pageNumber));
      favBtn.setAttribute('data-pw-fav-thumb', result.coverImage || '');
      favBtn.setAttribute('aria-label', 'Favorite');
      favBtn.setAttribute('aria-pressed', 'false');
      var favGlyph = el('span', '', '♡');
      favGlyph.setAttribute('data-pw-fav-glyph', '');
      favGlyph.setAttribute('aria-hidden', 'true');
      favBtn.appendChild(favGlyph);
      tile.appendChild(favBtn);
    }
    card.appendChild(tile);

    // Title
    card.appendChild(el('p', 'pw-image-search__card-title', result.title || 'Untitled page'));

    // "from {book} →" link to the parent product PDP
    if (result.parentHandle) {
      var link = el('a', 'pw-image-search__from', '');
      link.href = '/products/' + encodeURIComponent(result.parentHandle);
      link.textContent = 'from ' + (result.parentTitle || result.parentHandle) + ' →';
      card.appendChild(link);
    }

    // "Add page" button — POSTs a real /cart/add.js via window.PWPageCart (S4, wired 2026-07-02).
    // Per-item price dropped 2026-07-13 (Paul): a price on every one of 20+ cards was noise --
    // the tiered rule now shows once, near the top (pw-page-pricing-tiers.liquid).
    var addBtn = el('button', 'pw-image-search__add', 'Add Page to Cart');
    addBtn.type = 'button';
    addBtn.setAttribute('data-pw-add-page', '');
    addBtn.setAttribute('data-page-id', result.id || '');
    addBtn.setAttribute('data-parent-handle', result.parentHandle || '');
    addBtn.setAttribute('data-page-number', result.pageNumber != null ? String(result.pageNumber) : '');
    addBtn.setAttribute('data-cover-image', result.coverImage || '');
    // F2, 2026-07-08 cross-catalog search cart-line title fix — mirrors pw-book-pages.js's
    // data-book-title stamp so PWPageCart's shared add-to-cart handler shows the real book
    // title on the cart line instead of a humanized-handle guess.
    if (result.parentTitle) addBtn.setAttribute('data-book-title', result.parentTitle);
    addBtn.setAttribute('data-variant', variantId || '');
    if (result.price) {
      addBtn.setAttribute('data-price-amount', result.price.amount || '');
      addBtn.setAttribute('data-price-currency', result.price.currencyCode || '');
    }
    card.appendChild(addBtn);

    return card;
  }

  function render(container, results, variantId, append) {
    if (!append) container.textContent = '';
    var frag = document.createDocumentFragment();
    for (var i = 0; i < results.length; i++) {
      // Only render page-kind results in the image grid.
      if (results[i] && results[i].type === 'page') frag.appendChild(buildCard(results[i], variantId));
    }
    container.appendChild(frag);
  }

  // PW (Paul, 2026-07-03, bugs #10/#15): the backend already paginates
  // (returns total/page/pages) — the grid just never asked for page 2+, which
  // both hid results past the first page AND made the real total look far
  // smaller than what's actually indexed ("I never see 18k+ images"). Adds a
  // "Load more" control, used by init()'s runImageSearch below.
  function updateLoadMore(resultsEl, shownCount, total, hasMore, onClick) {
    var btn = resultsEl.parentNode.querySelector('.pw-image-search__load-more');
    if (!hasMore) {
      if (btn) btn.remove();
      return;
    }
    if (!btn) {
      btn = el('button', 'pw-image-search__load-more', '');
      btn.type = 'button';
      resultsEl.insertAdjacentElement('afterend', btn);
    }
    btn.textContent = 'Load more (' + shownCount + ' of ' + total + ')';
    btn.disabled = false;
    btn.onclick = onClick;
  }

  function init(root) {
    if (!root || root.dataset.pwInit === 'true') return;
    root.dataset.pwInit = 'true';

    var form = root.querySelector('[data-pw-form]');
    var input = form && form.querySelector('input[name="q"]');
    var clearBtn = root.querySelector('.pw-image-search__clear');
    var results = root.querySelector('[data-pw-results]');
    var status = root.querySelector('[data-pw-status]');
    var sortGroup = root.querySelector('[data-pw-sort]');
    var priceTiers = root.querySelector('[data-pw-price-tiers]');
    var backendUrl = (root.dataset.backendUrl || '').replace(/\/+$/, '');
    var pageVariantId = root.dataset.pageVariantId || '';
    if (!form || !input || !results) return;

    // Fix 2026-08-04: real, custom, cross-browser clear button — see the markup comment in
    // pw-image-search.liquid for why the native one wasn't enough. Visibility tracks content;
    // clicking never touches the search-mode radios, so it can't silently switch modes.
    if (clearBtn) {
      var syncClearVisibility = function () {
        clearBtn.hidden = !input.value;
      };
      syncClearVisibility();
      input.addEventListener('input', syncClearVisibility);
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        input.focus();
        if (currentMode() === 'image') {
          results.textContent = '';
          setStatus(status, '');
          syncSearchUrl('', true);
        }
      });
    }

    var inFlight = null; // guards double-submits / stale responses
    var shownCount = 0;
    var currentSort = 'relevance';
    // T2 Phase 1 — this page becomes the target of /search's Products/Images redirect (Phase 2),
    // so it needs to accept everything that link carries: the query, and the active curiosity/tag
    // scope (bugs #3/#5's guard). A closure var, read fresh on every runImageSearch call below
    // (including the sort-change and Load More call sites), so scope survives those too — see
    // plans/2026-08-05-single-image-search-plan-REFRESHED.md section 2.
    var scopeTags = new URLSearchParams(location.search).get('scope') || '';

    results.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pw-add-page]');
      if (btn && window.PWPageCart) window.PWPageCart.addPageToCart(btn);
      // Phase 2A: a click anywhere on the card (cover/title/"from" link) is a select_item —
      // deliberately excludes the add-to-cart button itself, a distinct add_to_cart intent.
      if (!btn) {
        var card = e.target.closest('.pw-image-search__card');
        var handle = card && card.getAttribute('data-page-id');
        if (handle) postCollectEvent(backendUrl, { event: 'select_item', handle: handle });
      }
    });

    if (sortGroup) {
      sortGroup.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-pw-sort-value]');
        if (!btn) return;
        var value = btn.getAttribute('data-pw-sort-value');
        if (value === currentSort) return;
        currentSort = value;
        sortGroup.querySelectorAll('[data-pw-sort-value]').forEach(function (b) {
          b.classList.toggle('pw-image-search__sort-btn--active', b === btn);
        });
        var query = (input.value || '').trim();
        if (query) runImageSearch(query);
      });
    }

    function currentMode() {
      var checked = form.querySelector('input[name="pw_search_mode"]:checked');
      return checked ? checked.value : 'product';
    }

    async function runImageSearch(query, page) {
      page = page || 1;
      if (forcesProductResults(query)) {
        window.location.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
        return;
      }
      if (!backendUrl) {
        setStatus(status, 'Image search is not configured.');
        return;
      }
      var token = {};
      inFlight = token;
      var append = page > 1;
      if (!append) { setStatus(status, 'Searching…'); shownCount = 0; }

      var url = backendUrl + '/api/search?type=page&q=' + encodeURIComponent(query) + '&page=' + page;
      if (scopeTags) url += '&scope=' + encodeURIComponent(scopeTags);
      if (currentSort === 'title-ascending') url += '&sort=title-ascending';
      // Amy, item 10, 2026-07-24: "Newest" sort for Images mode — same fixed-literal branch
      // shape as title-ascending above, deliberately not a generalized concatenation of the
      // sort variable onto the query string (see the az-sort test's explicit guard on this).
      if (currentSort === 'created-descending') url += '&sort=created-descending';
      try {
        var resp = await fetch(url, { headers: { Accept: 'application/json' } });
        if (inFlight !== token) return; // a newer query superseded this one
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        var data = await resp.json();
        if (inFlight !== token) return;

        var list = (data && Array.isArray(data.results) ? data.results : []).filter(function (r) {
          return r && r.type === 'page';
        });

        if (!append && list.length === 0) {
          setStatus(status, 'No images found — try another search.');
          results.textContent = '';
          updateLoadMore(results, 0, 0, false);
          postCollectEvent(backendUrl, { event: 'zero_results', query: query });
          return;
        }
        shownCount += list.length;
        var total = (data && typeof data.total === 'number') ? data.total : shownCount;
        var hasMore = (data && typeof data.pages === 'number' && typeof data.page === 'number')
          ? data.page < data.pages
          : false;
        setStatus(status, 'Showing ' + shownCount + ' of ' + total + (total === 1 ? ' image' : ' images'));
        render(results, list, pageVariantId, append);
        updateLoadMore(results, shownCount, total, hasMore, function () { runImageSearch(query, page + 1); });
        if (!append) {
          postCollectEvent(backendUrl, { event: 'search', query: query, resultCount: total });
          postCollectEvent(backendUrl, { event: 'view_item_list', query: query, resultCount: total });
        }
      } catch (err) {
        if (inFlight !== token) return;
        setStatus(status, 'Something went wrong searching images. Please try again.');
        if (!append) results.textContent = '';
      } finally {
        if (inFlight === token) inFlight = null;
      }
    }

    /**
     * Keep the address bar in step with what the user is actually looking at (2026-08-02).
     *
     * Paul: "if you change term and search again it reverts to All Products." An image search is
     * intercepted in JS and never navigates, so nothing wrote the mode or the term into the URL —
     * the address bar still said ?q=old&type=product. Any reload, back/forward, or native submit
     * then re-entered PRODUCTS mode, because the URL is the only state that survives a navigation
     * and it had never learned the user switched.
     *
     * This file already knew that: the comment above says mode is URL state, and the result pills
     * set mode=image on their hrefs. The search submit itself was simply never made to do it.
     * Third report of this class (Amy 2026-08-01, the Projects form earlier tonight, now this).
     *
     * replaceState, not pushState: re-searching is a refinement, not a new place — pushing would
     * make Back walk through every keystroke-worth of search instead of leaving the page.
     */
    function syncSearchUrl(query, isImage) {
      if (!window.history || !window.history.replaceState || !window.location) return;
      try {
        var url = new URL(window.location.href);
        if (query) url.searchParams.set('q', query);
        if (isImage) url.searchParams.set('mode', 'image');
        else url.searchParams.delete('mode');
        window.history.replaceState(null, '', url.toString());
      } catch (e) {
        /* URL/history unavailable — the search itself must still run */
      }
    }

    // Toggle changes: clear the image grid when switching back to Products; show/hide sort.
    form.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'pw_search_mode') {
        var isImage = currentMode() === 'image';
        if (sortGroup) sortGroup.hidden = !isImage;
        if (priceTiers) priceTiers.hidden = !isImage;
        syncSearchUrl((input.value || '').trim(), isImage);
        if (!isImage) {
          results.textContent = '';
          setStatus(status, '');
        }
      }
    });

    form.addEventListener('submit', function (e) {
      if (currentMode() !== 'image') return; // Products mode → let the native GET happen
      e.preventDefault();
      var query = (input.value || '').trim();
      if (!query) {
        setStatus(status, 'Type something to search images.');
        results.textContent = '';
        return;
      }
      syncSearchUrl(query, true);
      runImageSearch(query);
    });

    // Pre-select Images mode when linked in with ?mode=image (e.g. the "Shop Individual
    // Pages" nav item, or — T2 Phase 2 — the /search redirect link). Without a ?q=, this still
    // only puts the toggle + focus in the right place, unchanged. With one (the redirect case),
    // auto-run: the visitor already typed and submitted a search on /search: making them retype
    // it here would be the exact regression bug #45 exists to prevent.
    if (new URLSearchParams(location.search).get('mode') === 'image') {
      var imageRadio = form.querySelector('input[data-pw-mode="image"]');
      if (imageRadio) {
        imageRadio.checked = true;
        if (sortGroup) sortGroup.hidden = false;
        if (priceTiers) priceTiers.hidden = false;
        var urlQuery = new URLSearchParams(location.search).get('q') || '';
        if (urlQuery) {
          input.value = urlQuery;
          if (clearBtn) clearBtn.hidden = false;
          runImageSearch(urlQuery);
        } else {
          input.focus();
        }
      }
    }
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('pw-image-search');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  // Amy (2026-07-11): A-Z is now offered on the native /search sort row too (was Images-mode
  // only). Shopify's /search route still can't sort server-side on title (Bug #6, still true --
  // see pw-results-heading.liquid), so on load: (a) recompute every sort pill's active class from
  // the REAL sort_by query param, since search.sort_by silently normalizes an unrecognized key
  // back to its own default (same root cause as the 2026-07-06 Images-mode active-pill bug, now
  // also true for Products mode since it never had a client-only sort key before this), and
  // (b) when in Products mode with sort_by=title-ascending, re-sort the current page's already-
  // rendered cards client-side. Scope: native /search is paginated, not infinite-scroll (see
  // templates/search.json), so this only sorts the current page -- it doesn't fetch/merge results
  // across pages.
  function applySearchAzSort() {
    if (location.pathname !== '/search') return;
    var actualSort = new URLSearchParams(location.search).get('sort_by') || 'relevance';
    document.querySelectorAll('.pw-sort-pill[href]').forEach(function (pill) {
      var pillSort = new URL(pill.getAttribute('href'), location.href).searchParams.get('sort_by') || 'relevance';
      pill.classList.toggle('pw-sort-pill--active', pillSort === actualSort);
    });
    if (actualSort !== 'title-ascending') return;
    if (document.documentElement.getAttribute('data-pw-mode') === 'image') return;
    var items = Array.prototype.slice.call(
      document.querySelectorAll('.product-grid__item[data-product-title]')
    );
    if (items.length < 2) return;
    var parent = items[0].parentNode;
    items.sort(function (a, b) {
      return a.getAttribute('data-product-title').localeCompare(
        b.getAttribute('data-product-title'),
        undefined,
        { sensitivity: 'base' }
      );
    });
    items.forEach(function (item) { parent.appendChild(item); });
  }

  // Paul (2026-07-27): Newest pill on /search -- same gap and same fix as A-Z above.
  // created-descending isn't a real Shopify /search sort_by key either, so this is also a
  // client-side re-sort of the current page's already-rendered cards, using the
  // data-product-created-at attribute (search-results.liquid) the same way A-Z's sort uses
  // data-product-title. Same scope limits: current page only, Products mode only.
  function applySearchNewestSort() {
    if (location.pathname !== '/search') return;
    var actualSort = new URLSearchParams(location.search).get('sort_by') || 'relevance';
    if (actualSort !== 'created-descending') return;
    if (document.documentElement.getAttribute('data-pw-mode') === 'image') return;
    var items = Array.prototype.slice.call(
      document.querySelectorAll('.product-grid__item[data-product-created-at]')
    );
    if (items.length < 2) return;
    var parent = items[0].parentNode;
    items.sort(function (a, b) {
      return Number(b.getAttribute('data-product-created-at')) -
        Number(a.getAttribute('data-product-created-at'));
    });
    items.forEach(function (item) { parent.appendChild(item); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
      applySearchAzSort();
      applySearchNewestSort();
    });
  } else {
    initAll(document);
    applySearchAzSort();
    applySearchNewestSort();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    applySearchAzSort();
    applySearchNewestSort();
  });
})();
